import { CACHE_TIERS } from "@workspace/types";
import { trpc } from "@/lib/trpc/react";

interface UseShowsInfiniteParams {
	search?: string;
	tab?: "now" | "upcoming" | "past" | "all";
	limit?: number;
	city?: string;
	startDate?: string;
	endDate?: string;
	venueId?: string;
	genres?: string[];
}

/**
 * Performance-optimized tRPC hook for fetching shows with infinite scroll
 *
 * Optimizations:
 * - Request deduplication via structuralSharing
 * - Smart refetch strategies
 * - Optimized network mode
 * - Better error retry logic
 */
export function useShowsInfinite({
	search = "",
	tab = "all",
	limit = 8,
	city,
	startDate,
	endDate,
	venueId,
	genres,
}: UseShowsInfiniteParams = {}): ReturnType<
	typeof trpc.show.list.useInfiniteQuery
> {
	return trpc.show.list.useInfiniteQuery(
		{
			search,
			tab,
			limit,
			city,
			startDate,
			endDate,
			venueId,
			genres,
		},
		{
			// Pagination
			getNextPageParam: (lastPage) => {
				if (!lastPage || !lastPage.hasNextPage) return undefined;
				return lastPage.nextCursor ?? undefined;
			},

			// Show list data should be cached for performance
			// Facepile data is handled separately via real-time subscriptions
			...CACHE_TIERS.DYNAMIC, // Dynamic content - discovery lists

			// Memory management - allow extensive show discovery
			maxPages: 300,

			// Request deduplication - prevents duplicate requests
			structuralSharing: true,

			// Network optimization
			networkMode: "offlineFirst", // Use cache first, then network

			// Improved error handling
			retry: (failureCount, error) => {
				// Don't retry on 4xx errors (client errors)
				if (error && typeof error === "object" && "status" in error) {
					const status = (error as any).status;
					if (status >= 400 && status < 500) {
						return false;
					}
				}
				// Retry up to 2 times for other errors
				return failureCount < 2;
			},
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

			// Keep previous data while fetching
			placeholderData: (previousData) => previousData,

			// Only re-render on data/error changes, not isRefetching
			notifyOnChangeProps: ["data", "error", "isFetchingNextPage"],
		},
	);
}
